import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  borrarToken,
  guardarToken,
  leerToken,
  ErrorApi,
} from "../lib/api";
import { olvidarIdiomaGuardado, type Idioma } from "../lib/idioma";

export interface SocioPortal {
  id: string;
  numeroSocio: number;
  nombre: string;
  apellidos: string;
  estado: string;
  fechaAlta: string;
  fechaCaducidad: string | null;
  saldoCentimos: number;
  fotoUrl: string | null;
  /** El suyo, si lo ha elegido. Null = el del club. */
  idioma: Idioma | null;
  codigoAcceso: string;
  tipoSocio: {
    nombre: string;
    color: string;
    descuentoPorcentaje: number;
  } | null;
}

export interface ClubPortal {
  nombre: string;
  slug: string;
  logoUrl: string | null;
  telefono: string | null;
  direccion: string | null;
  ciudad: string | null;
  /** El idioma en que el club habla a sus socios; lo pone en Ajustes. */
  idioma?: Idioma;
}

export interface Consumo {
  limiteDiarioMg: number;
  limiteMensualMg: number;
  consumidoHoyMg: number;
  consumidoMesMg: number;
  disponibleMg: number;
}

export interface DatosPortal {
  socio: SocioPortal;
  club: ClubPortal;
  consumo: Consumo;
  bloqueo: string | null;
}

interface ValorSesionPortal {
  datos: DatosPortal | null;
  cargando: boolean;
  entrar: (
    club: string,
    numeroSocio: number,
    password: string,
  ) => Promise<void>;
  salir: () => Promise<void>;
  refrescar: () => Promise<void>;
}

const Contexto = createContext<ValorSesionPortal | null>(null);

/** El club se recuerda para no tener que teclearlo cada vez. */
export const CLAVE_CLUB = "clubs.portal.club";

export function ProveedorSesionPortal({ children }: { children: ReactNode }) {
  const [datos, setDatos] = useState<DatosPortal | null>(null);
  const [cargando, setCargando] = useState(true);

  const refrescar = useCallback(async () => {
    if (!leerToken()) {
      setDatos(null);
      setCargando(false);
      return;
    }
    try {
      setDatos(await api<DatosPortal>("/portal/yo"));
    } catch (fallo) {
      // Solo un 401 cierra la sesión; un fallo de red no debe echar al socio.
      if (fallo instanceof ErrorApi && fallo.status === 401) {
        borrarToken();
        setDatos(null);
      }
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void refrescar();
  }, [refrescar]);

  const entrar = useCallback(
    async (club: string, numeroSocio: number, password: string) => {
      const respuesta = await api<{ token: string; club: ClubPortal }>(
        "/portal/login",
        {
          metodo: "POST",
          cuerpo: { club, numeroSocio, password },
        },
      );
      guardarToken(respuesta.token);
      localStorage.setItem(CLAVE_CLUB, club);
      await refrescar();
    },
    [refrescar],
  );

  const salir = useCallback(async () => {
    await api("/portal/logout", { metodo: "POST" }).catch(() => undefined);
    borrarToken();
    // Y el idioma: en la tablet del mostrador, detras de este socio entra otro.
    olvidarIdiomaGuardado();
    setDatos(null);
  }, []);

  const valor = useMemo(
    () => ({ datos, cargando, entrar, salir, refrescar }),
    [datos, cargando, entrar, salir, refrescar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesionPortal(): ValorSesionPortal {
  const valor = useContext(Contexto);
  if (!valor)
    throw new Error(
      "useSesionPortal debe usarse dentro de ProveedorSesionPortal",
    );
  return valor;
}
